/**
 * Lightweight Pagination Library
 * Alternative to PaginationJS
 */
export default class Pagination {
  constructor(element, options = {}) {
    this.element = typeof element === 'string' ? document.querySelector(element) : element;

    if (!this.element) {
      console.error('Pagination: Element not found');
      return;
    }

    // Default options
    this.options = {
      dataSource: options.dataSource || [],
      pageSize: options.pageSize || 10,
      pageRange: options.pageRange || 2,
      showPageNumbers: options.showPageNumbers !== false,
      showPrevNext: options.showPrevNext !== false,
      showFirstLast: options.showFirstLast !== false,
      prevText: options.prevText || '‹',
      nextText: options.nextText || '›',
      firstText: options.firstText || '«',
      lastText: options.lastText || '»',
      className: options.className || 'pagination',
      activeClassName: options.activeClassName || 'active',
      disabledClassName: options.disabledClassName || 'disabled',
      callback: options.callback || function () { },
      afterPaging: options.afterPaging || null,
      afterRender: options.afterRender || null,
      formatResult: options.formatResult || null
    };

    this.currentPage = 1;
    this.totalPages = Math.ceil(this.options.dataSource.length / this.options.pageSize);

    this.init();
  }

  init() {
    this.render();
    this.goToPage(1);
  }

  render() {
    if (!this.element) return;

    const paginationHTML = this.createPaginationHTML();
    this.element.innerHTML = paginationHTML;
    this.attachEventListeners();

    if (this.options.afterRender) {
      this.options.afterRender();
    }
  }

  createPaginationHTML() {
    if (this.totalPages <= 1) {
      return ''; // No pagination needed
    }

    let html = `<ul class="${this.options.className}">`;

    // First button
    if (this.options.showFirstLast) {
      const disabledClass = this.currentPage === 1 ? this.options.disabledClassName : '';
      html += `<li class="${disabledClass}">
        <a href="javascript:void(0);" data-page="1" aria-label="Erste">
          ${this.options.firstText}
        </a>
      </li>`;
    }

    // Previous button
    if (this.options.showPrevNext) {
      const disabledClass = this.currentPage === 1 ? this.options.disabledClassName : '';
      html += `<li class="${disabledClass}">
        <a href="javascript:void(0);" data-page="${this.currentPage - 1}" aria-label="voriges Thema">
          ${this.options.prevText}
        </a>
      </li>`;
    }

    // Page numbers
    if (this.options.showPageNumbers) {
      const pageNumbers = this.getPageNumbers();
      pageNumbers.forEach(page => {
        if (page === '...') {
          html += `<li class="${this.options.disabledClassName}">
            <span class="pager-link">...</span>
          </li>`;
        } else {
          const activeClass = page === this.currentPage ? this.options.activeClassName : '';
          html += `<li class="${activeClass}">
            <a href="javascript:void(0);" data-page="${page}">${page}</a>
          </li>`;
        }
      });
    }

    // Next button
    if (this.options.showPrevNext) {
      const disabledClass = this.currentPage === this.totalPages ? this.options.disabledClassName : '';
      html += `<li class="${disabledClass}">
        <a href="javascript:void(0);" data-page="${this.currentPage + 1}" aria-label="nächstes Thema">
          ${this.options.nextText}
        </a>
      </li>`;
    }

    // Last button
    if (this.options.showFirstLast) {
      const disabledClass = this.currentPage === this.totalPages ? this.options.disabledClassName : '';
      html += `<li class="${disabledClass}">
        <a href="javascript:void(0);" data-page="${this.totalPages}" aria-label="Letzte">
          ${this.options.lastText}
        </a>
      </li>`;
    }

    html += '</ul>';
    return html;
  }

  getPageNumbers() {
    const pages = [];
    const range = this.options.pageRange;

    // If total pages is small, show all
    if (this.totalPages <= (range * 2 + 5)) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let startPage = Math.max(2, this.currentPage - range);
    let endPage = Math.min(this.totalPages - 1, this.currentPage + range);

    // Add ellipsis if needed after first page
    if (startPage > 2) {
      pages.push('...');
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis if needed before last page
    if (endPage < this.totalPages - 1) {
      pages.push('...');
    }

    // Always show last page
    if (this.totalPages > 1) {
      pages.push(this.totalPages);
    }

    return pages;
  }

  attachEventListeners() {
    if (!this.element) return;

    // Use event delegation on the container instead of individual links
    const paginationList = this.element.querySelector('ul');

    if (!paginationList) return;

    // Remove old listener if exists
    if (this.boundClickHandler) {
      paginationList.removeEventListener('click', this.boundClickHandler);
    }

    // Create bound handler
    this.boundClickHandler = (e) => {
      // Check if clicked element is a link or inside a link
      const link = e.target.closest('a[data-page]');

      if (!link) return;

      // Prevent default behavior immediately
      e.preventDefault();
      e.stopPropagation();

      // Don't do anything if disabled
      const parentItem = link.closest('li');
      if (parentItem && parentItem.classList.contains(this.options.disabledClassName)) {
        return false;
      }

      const page = parseInt(link.dataset.page, 10);

      if (!isNaN(page) && page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.goToPage(page);
      }

      return false;
    };

    // Attach event listener
    paginationList.addEventListener('click', this.boundClickHandler);
  }

  goToPage(page) {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    const start = (page - 1) * this.options.pageSize;
    const end = start + this.options.pageSize;
    const pageData = this.options.dataSource.slice(start, end);

    // Re-render pagination controls
    this.render();

    // Call callback with filtered data
    this.options.callback(pageData, {
      pageNumber: this.currentPage,
      pageSize: this.options.pageSize,
      pageRange: [start, end],
      totalNumber: this.options.dataSource.length,
      totalPage: this.totalPages
    });

    if (this.options.afterPaging) {
      this.options.afterPaging(this.currentPage);
    }
  }

  // Public methods for external control
  refresh(newDataSource) {
    this.options.dataSource = newDataSource || this.options.dataSource;
    this.totalPages = Math.ceil(this.options.dataSource.length / this.options.pageSize);
    this.currentPage = 1;
    this.init();
  }

  next() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  previous() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  first() {
    this.goToPage(1);
  }

  last() {
    this.goToPage(this.totalPages);
  }

  destroy() {
    // Remove event listener
    if (this.boundClickHandler && this.element) {
      const paginationList = this.element.querySelector('ul');
      if (paginationList) {
        paginationList.removeEventListener('click', this.boundClickHandler);
      }
    }

    if (this.element) {
      this.element.innerHTML = '';
    }
  }
}